import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { FileText, History, RefreshCcw, Search } from 'lucide-react';

import { EmptyState } from '../../components/admin/EmptyState';
import { PromptVersionsDialog } from '../../components/admin/system-prompt/PromptVersionsDialog';
import { useSystemPrompts } from '../../services/systemPrompt.service';
import { SystemPrompt } from '../../types/admin';

type PendingFilter = 'all' | 'pending' | 'synced';

const promptPreview = (value: string | null | undefined) => {
  const text = (value || '').trim();
  return text.length > 120 ? `${text.slice(0, 117)}...` : text || '-';
};

export const SystemPromptManagement: React.FC = () => {
  const { data: prompts = [], isLoading, refetch } = useSystemPrompts();
  const [selectedPrompt, setSelectedPrompt] = useState<SystemPrompt | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [pending, setPending] = useState<PendingFilter>('all');

  const categories = useMemo(() => {
    return Array.from(new Set(prompts.map((prompt) => prompt.category))).sort();
  }, [prompts]);

  const filteredPrompts = useMemo(() => {
    const query = search.trim().toLowerCase();

    return prompts.filter((prompt) => {
      const matchesSearch =
        !query ||
        prompt.key.toLowerCase().includes(query) ||
        prompt.name.toLowerCase().includes(query) ||
        (prompt.description || '').toLowerCase().includes(query);
      const matchesCategory = category === 'all' || prompt.category === category;
      const matchesPending =
        pending === 'all' || (pending === 'pending') === prompt.hasPendingVersion;

      return matchesSearch && matchesCategory && matchesPending;
    });
  }, [category, pending, prompts, search]);

  // Keep the dialog on fresh data after activate/create refetches the list.
  const dialogPrompt = useMemo(
    () => prompts.find((item) => item.key === selectedPrompt?.key) ?? selectedPrompt,
    [prompts, selectedPrompt]
  );

  const handleOpen = (prompt: SystemPrompt) => {
    setSelectedPrompt(prompt);
    setDialogOpen(true);
  };

  const columns: GridColDef<SystemPrompt>[] = [
    {
      field: 'key',
      headerName: 'Key',
      flex: 1,
      minWidth: 240,
      renderCell: (params) => (
        <Box className="min-w-0">
          <Typography
            variant="body2"
            className="truncate font-mono text-gray-900 dark:text-slate-100"
          >
            {params.row.key}
          </Typography>
          <Typography variant="caption" className="truncate text-slate-500 dark:text-slate-400">
            {params.row.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1.2,
      minWidth: 240,
      renderCell: (params) => (
        <Tooltip title={params.row.description || ''}>
          <Typography variant="body2" className="truncate text-slate-700 dark:text-slate-300">
            {params.row.description || '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'content',
      headerName: 'Active Content',
      flex: 1,
      minWidth: 240,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={params.row.content}>
          <Typography variant="body2" className="truncate text-slate-700 dark:text-slate-300">
            {promptPreview(params.row.content)}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 130,
      renderCell: (params) => <Chip label={params.value} size="small" variant="outlined" />,
    },
    {
      field: 'activeVersion',
      headerName: 'Active',
      width: 90,
      align: 'center',
      valueFormatter: (value: number | null) => (value === null ? '-' : `v${value}`),
    },
    {
      field: 'latestVersion',
      headerName: 'Latest',
      width: 90,
      align: 'center',
      valueFormatter: (value: number) => `v${value}`,
    },
    {
      field: 'hasPendingVersion',
      headerName: 'State',
      width: 130,
      renderCell: (params) => (
        <Tooltip
          title={
            params.value
              ? 'A newer version exists that is not active yet.'
              : 'Runtime serves the latest version.'
          }
        >
          <Chip
            label={params.value ? 'Pending' : 'Up to date'}
            size="small"
            color={params.value ? 'warning' : 'success'}
          />
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: 'Versions',
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => handleOpen(params.row)}
          className="text-gray-600 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700"
        >
          <History size={18} />
        </IconButton>
      ),
    },
  ];

  return (
    <Box className="p-6">
      <Box className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <Box>
          <Typography variant="h4" className="font-bold text-gray-900 dark:text-slate-100">
            System Prompts
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Versioned runtime prompts. Only the active version is served.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshCcw size={18} />} onClick={() => refetch()}>
          Refresh
        </Button>
      </Box>

      <Box className="mb-4 grid gap-3 rounded-lg bg-white p-4 shadow-sm dark:bg-slate-800 md:grid-cols-[minmax(0,1fr)_180px_160px]">
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <Search size={18} className="mr-2 text-slate-400" />,
          }}
        />
        <FormControl size="small">
          <InputLabel>Category</InputLabel>
          <Select value={category} label="Category" onChange={(e) => setCategory(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            {categories.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small">
          <InputLabel>State</InputLabel>
          <Select
            value={pending}
            label="State"
            onChange={(e) => setPending(e.target.value as PendingFilter)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="synced">Up to date</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box className="overflow-x-auto rounded-xl bg-white shadow-sm dark:bg-slate-800">
        <DataGrid
          rows={filteredPrompts}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          getRowId={(row) => row.key}
          className="border-none"
          slots={{
            noRowsOverlay: () => (
              <EmptyState
                Icon={FileText}
                title="No system prompts"
                description="No runtime prompts match the current filters."
              />
            ),
          }}
        />
      </Box>

      <PromptVersionsDialog
        prompt={dialogPrompt}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
};

export default SystemPromptManagement;
